import { client } from '@api/client';
import { URL } from '@api/urls';

import { TDefaultDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IDashboardOrganizationRentGeneralDTO,
	IDashboardRentGeneralDTO,
	IDashboardRequestStatusSummaryByStatusDTO,
	IDashboardRequestStatusSummaryExportDTO,
	IExportFileDTO,
	IOrganizationExportFileDTO,
	TDashboardAssetTypeSummaryDTO,
	TDashboardAssetTypeSummaryPerOrganizationDTO,
	TDashboardRentGeneralExportDTO,
	TDashboardSummaryPerOrganizationDTO,
	TDashboardSummaryPerOrganizationExportDTO,
} from '@interfaces/platform/dto/platformDashboardDTO';
import {
	IAssetTypesOrganizationDashboard,
	IDashboardAssetSummary,
	IDashboardRentsGeneralSummary,
	IDashboardRequestStatusSummary,
	IDashboardRequestSummaryByStatus,
	TDashboardAssetSummaryPerOrganization,
	TDashboardAssetTypeSummaryPerOrganization,
	TDashboardRentSummary,
} from '@interfaces/platform/platformDashboard';

export const getDashboardAssetsSummary = (): Promise<IDashboardAssetSummary> => {
	return client.get(`${URL.dashboardAssetSummary}`);
};

export const getDashboardAssetSummaryPerOrganization = (
	params: TDashboardSummaryPerOrganizationDTO,
): Promise<IItemsResponse<TDashboardAssetSummaryPerOrganization>> => {
	return client.get(`${URL.dashboardAssetSummary}/per-organization`, { params });
};

export const getDashboardRentGeneralSummary = (
	params: IDashboardRentGeneralDTO,
): Promise<IDashboardRentsGeneralSummary> => {
	return client.get(`${URL.dashboardRentSummary}/general`, { params });
};

export const getDashboardRentsSummary = (params: IDashboardRentGeneralDTO): Promise<TDashboardRentSummary[]> => {
	return client.get(`${URL.dashboardRentSummary}/organisations`, { params });
};

export const getDashboardRequestStatusesSummaryByStatus = (
	params: IDashboardRequestStatusSummaryByStatusDTO,
): Promise<IItemsResponse<IDashboardRequestSummaryByStatus>> => {
	return client.get(`${URL.dashboardRequestStatusSummary}/organisation/by-status`, { params });
};

export const getDashboardRequestStatusesSummary = (): Promise<IDashboardRequestStatusSummary[]> => {
	return client.get(`${URL.dashboardRequestStatusSummary}/organisations`);
};

export const getDashboardAssetTypesSummaryPerOrganizations = (
	params: TDashboardAssetTypeSummaryPerOrganizationDTO,
): Promise<IItemsResponse<TDashboardAssetTypeSummaryPerOrganization>> => {
	const { id, ...dto } = params;

	return client.get(`${URL.dashboardAssetType}/${id}/summary/organisations`, { params: dto });
};

export const getDashboardAssetTypesSummary = (params: TDefaultDTO): Promise<TDashboardAssetTypeSummaryDTO> => {
	return client.get(`${URL.dashboardAssetType}/summary/organisation/by-type`, { params });
};

export const getDashboardAssetSummaryPerOrganizationExport = (
	params: TDashboardSummaryPerOrganizationExportDTO,
): Promise<void> => {
	return client.get(`${URL.dashboardAssetSummary}/per-organization/export`, { params });
};

export const getDashboardRentsSummaryExport = (params: TDashboardRentGeneralExportDTO): Promise<void> => {
	return client.post(`${URL.dashboardRentSummary}/organisations/export`, null, { params });
};

export const getDashboardRequestStatusesSummaryExport = (params: IExportFileDTO): Promise<void> => {
	return client.get(`${URL.dashboardRequestStatusSummary}/organisations/export`, { params });
};

export const getDashboardRequestStatusSummaryExport = (
	params: IDashboardRequestStatusSummaryExportDTO,
): Promise<void> => {
	const { id, ...dto } = params;

	return client.get(`${URL.dashboardRequestStatusSummary}/organisations/${id}/export`, { params: dto });
};

export const getDashboardAssetTypesSummaryExport = (params: IExportFileDTO): Promise<void> => {
	return client.get(`${URL.dashboardAssetType}/summary/organisation/by-type/export`, { params });
};

export const getOrganizationDashboardAssetSummary = (
	id: string,
): Promise<IDashboardAssetSummary> => {
	return client.get(`${URL.dashboardAssetSummary}/organisation/${id}`);
};

export const getOrganizationDashboardAssetTypesSummary = (
	id: string,
): Promise<IAssetTypesOrganizationDashboard[]> => {
	return client.get(`${URL.dashboardAssetType}/summary/organisation/${id}`);
};

export const getOrganizationDashboardRentsSummary = (
	params: IDashboardOrganizationRentGeneralDTO,
): Promise<TDashboardRentSummary> => {
	const { id, ...rest } = params;

	return client.get(`${URL.dashboardRentSummary}/organisation/${id}`, { params: rest });
};

export const getOrganizationDashboardRequestStatusesSummary = (
	id: string,
): Promise<IDashboardRequestStatusSummary[]> => {
	return client.get(`${URL.dashboardRequestStatusSummary}/organisation/${id}`);
};

export const getOrganizationDashboardAssetTypesSummaryExport = (dto: IOrganizationExportFileDTO): Promise<void> => {
	const { id, ...params } = dto;

	return client.get(`${URL.dashboardAssetType}/summary/organisation/${id}/export`, { params });
};

export const getOrganizationDashboardRequestStatusesSummaryExport = (
	dto: IOrganizationExportFileDTO,
): Promise<void> => {
	const { id, ...params } = dto;

	return client.get(`${URL.dashboardRequestStatusSummary}/organisation/${id}/export`, { params });
};
